import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Offcanvas,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Breadcrumb from "Common/BreadCrumb";
import {
  addCategory,
  deleteCategory,
  fetchAllCategories,
  getAllProducts,
  updateCategory,
} from "api/apis";
import { CategoryInput } from "interface";

const Categories = () => {
  document.title = "Categories";

  const [show, setShow] = useState(false);
  const [info, setInfo] = useState<any>([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [currentpages, setCurrentpages] = useState<any[]>([]);
  const [pagination, setPagination] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const perPageData = 9;
  const [products, setProducts] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [editSelectedImage, setEditSelectedImage] = useState<File | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const handleDeleteClick = (categoryId: string) => {
    setCategoryToDelete(categoryId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteCategory(categoryToDelete);
      setAllCategories((prevCategories) =>
        prevCategories.filter((cat) => cat._id !== categoryToDelete)
      );
      alert("Category deleted successfully!");
    } catch (error) {
      console.error("Error deleting category:", error);
      alert("Failed to delete category. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };
  // const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [addSelectedProductIds, setAddSelectedProductIds] = useState<string[]>(
    []
  );
  const [editSelectedProductIds, setEditSelectedProductIds] = useState<
    string[]
  >([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const handleEditClick = (category: any) => {
    setEditingCategory(category);
    setShowEditModal(true);
    setEditSelectedImage(null);

    // Pre-fill the EDIT form data with the category details
    setEditFormData({
      name: category.name,
      description: category.description,
      slug: category.slug,
      products: category.products,
      isActive: category.isActive,
    });

    // Reset the ADD form data
    setAddFormData({
      name: "",
      description: "",
      products: [],
      isActive: true,
    });

    // Reset the Add form's selected products
    setAddSelectedProductIds([]);

    // Set the selected product IDs for the edit form
    setEditSelectedProductIds(category.products);
  };

  const handleAddInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setAddFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [id]: value,
      // Remove the automatic slug generation
    }));
  };

  // Update your handleCheckboxChange functions
  const handleAddCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    setAddFormData((prev) => ({
      ...prev,
      [id]: checked,
    }));
  };

  const handleEditCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [id]: checked,
    }));
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    try {
      let categoryData: FormData | CategoryInput;

      if (editSelectedImage) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append("name", editFormData.name);
        formData.append("description", editFormData.description);
        formData.append("slug", editFormData.slug);
        formData.append("isActive", editFormData.isActive.toString());

        // Append each product ID separately
        editSelectedProductIds.forEach((id) => {
          formData.append("products", id);
        });

        formData.append("image", editSelectedImage);
        categoryData = formData;
      } else {
        // Use regular JSON
        categoryData = {
          name: editFormData.name,
          description: editFormData.description,
          slug: editFormData.slug,
          isActive: editFormData.isActive,
          products: editSelectedProductIds,
        };
      }

      const updatedCategory = await updateCategory(
        editingCategory._id,
        categoryData
      );

      // Update the state with the returned category data
      setAllCategories((prevCategories) =>
        prevCategories.map((cat) =>
          cat._id === editingCategory._id ? updatedCategory : cat
        )
      );

      // Reset the editing state
      setEditingCategory(null);
      setEditFormData({
        name: "",
        description: "",
        slug: "",
        products: [],
        isActive: true,
      });
      setEditSelectedProductIds([]);
      setEditSelectedImage(null);

      setShowEditModal(false);
      alert("Category updated successfully!");
    } catch (error: any) {
      console.error("Error updating category:", error);
      let errorMessage = "Failed to update category. Please try again.";

      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const [addFormData, setAddFormData] = useState({
    name: "",
    description: "",
    products: [] as string[],
    isActive: true,
  });

  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
    products: [] as string[],
    slug: "",
    isActive: true,
  });

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.currentTarget as HTMLFormElement;
    if (!form.checkValidity()) {
      e.stopPropagation();
      form.classList.add("was-validated");
      return;
    }

    try {
      let categoryData: CategoryInput | FormData;

      if (selectedImage) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append("name", addFormData.name);
        formData.append("description", addFormData.description);
        formData.append("isActive", addFormData.isActive.toString());

        // Append each product ID separately
        addSelectedProductIds.forEach((id) => {
          formData.append("products", id);
        });

        formData.append("image", selectedImage);
        categoryData = formData;
      } else {
        // Use regular JSON
        categoryData = {
          name: addFormData.name,
          description: addFormData.description,
          products: addSelectedProductIds,
          isActive: addFormData.isActive,
        };
      }

      const newCategory = await addCategory(categoryData);
      setAllCategories([...allCategories, newCategory]);

      // Reset form
      setAddFormData({
        name: "",
        description: "",
        products: [],
        isActive: true,
      });
      setAddSelectedProductIds([]);
      setSelectedImage(null);
      form.classList.remove("was-validated");
      alert("Category added successfully!");
    } catch (error) {
      console.error("Error adding category:", error);
      alert("Failed to add category. Please try again.");
    }
  };
  // Update form data when inputs change

  useEffect(() => {
    const loadData = async () => {
      try {
        const categories = await fetchAllCategories();

        // fetch products with required arguments (e.g., page number)
        const { products } = await getAllProducts(1, 100); // pass page 1 or your desired page
        setAllCategories(categories);
        setProducts(products); // ✅ Now this is just an array
      } catch (error) {
        console.error("Failed to load data", error);
      }
    };
    loadData();
  }, []);

  // Fetch categories from backend
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await fetchAllCategories();
        console.log(categories);
        setAllCategories(categories);
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    loadCategories();
  }, []);

  // Pagination
  const indexOfLast = currentPage * perPageData;
  const indexOfFirst = indexOfLast - perPageData;

  const currentdata = useMemo(
    () => allCategories.slice(indexOfFirst, indexOfLast),
    [allCategories, indexOfFirst, indexOfLast]
  );

  useEffect(() => {
    setCurrentpages(currentdata);
  }, [currentPage, currentdata]);

  const handleClick = (e: any) => {
    setCurrentPage(Number(e.target.id));
  };

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(allCategories.length / perPageData); i++) {
    pageNumbers.push(i);
  }

  const handleprevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handlenextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, pageNumbers.length));
  };

  useEffect(() => {
    if (pageNumbers.length && pageNumbers.length < currentPage) {
      setCurrentPage(pageNumbers.length);
    }
  }, [currentPage, pageNumbers.length]);

  // Search
  const searchTeamMember = (e: any) => {
    const search = e.target.value.toLowerCase();
    if (search) {
      setCurrentpages(
        allCategories.filter((cat) => cat.name.toLowerCase().includes(search))
      );
      setPagination(false);
    } else {
      setCurrentpages(currentdata);
      setPagination(true);
    }
  };

  const productOptions = products.map((product) => ({
    value: product._id,
    label: product.name,
  }));

  const getProductNamesByIds = (productIds: string[]) => {
    return productIds.map((id) => {
      const product = products.find((p) => p._id === id);
      return product ? product.name : "Unknown Product";
    });
  };

  const bufferToBase64 = (bufferArray: number[], contentType: string) => {
    if (!bufferArray || bufferArray.length === 0) return "";

    // Convert the Buffer array to a base64 string
    const base64String = btoa(
      bufferArray.reduce((data, byte) => data + String.fromCharCode(byte), "")
    );

    return `data:${contentType};base64,${base64String}`;
  };
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Categories" pageTitle="Products" />
          <Row>
            <Col xxl={3}>
              <Card>
                <Card.Header>
                  <h6 className="card-title mb-0">Create Categories</h6>
                </Card.Header>
                <Card.Body>
                  <form
                    autoComplete="off"
                    className="needs-validation createCategory-form"
                    id="createCategory-form"
                    noValidate
                    onSubmit={handleSubmit}
                  >
                    <Row>
                      <Col xxl={12}>
                        <div className="mb-3">
                          <label htmlFor="categoryName" className="form-label">
                            Category Name<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="name"
                            placeholder="Enter name"
                            required
                            value={addFormData.name}
                            onChange={handleAddInputChange}
                          />
                          <div className="invalid-feedback">
                            Please enter a category name.
                          </div>
                        </div>
                      </Col>
                      <Col xxl={12}>
                        <div className="mb-3">
                          <label htmlFor="description" className="form-label">
                            Description<span className="text-danger">*</span>
                          </label>
                          <textarea
                            className="form-control"
                            id="description"
                            rows={3}
                            placeholder="Description"
                            required
                            value={addFormData.description}
                            onChange={handleAddInputChange}
                          ></textarea>
                          <div className="invalid-feedback">
                            Please enter a description.
                          </div>
                        </div>
                      </Col>
                      <Col xxl={12}>
                        <div className="mb-3">
                          <label htmlFor="productSelect" className="form-label">
                            Select Products
                          </label>
                          <Select
                            isMulti
                            options={productOptions}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={(selectedOptions) => {
                              const selectedIds = selectedOptions.map(
                                (opt) => opt.value
                              );
                              setAddSelectedProductIds(selectedIds);
                            }}
                            value={productOptions.filter((option) =>
                              addSelectedProductIds.includes(option.value)
                            )}
                          />
                        </div>
                      </Col>

                      <Col xxl={12}>
                        <div className="mb-3">
                          <label htmlFor="categoryImage" className="form-label">
                            Category Image
                          </label>
                          <input
                            type="file"
                            className="form-control"
                            id="categoryImage"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                          {selectedImage && (
                            <div className="mt-2">
                              <img
                                src={URL.createObjectURL(selectedImage)}
                                alt="Preview"
                                className="img-thumbnail"
                                style={{ maxHeight: "150px" }}
                              />
                            </div>
                          )}
                        </div>
                      </Col>

                      <Col xxl={12}>
                        <div className="form-check mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isActive"
                            checked={addFormData.isActive}
                            onChange={handleAddCheckboxChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isActive"
                          >
                            Is Active
                          </label>
                        </div>
                      </Col>
                      <Col xxl={12}>
                        <div className="text-end">
                          <Button variant="success" type="submit">
                            Add Category
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </form>
                </Card.Body>
              </Card>
            </Col>

            <Col xxl={9}>
              <Row className="justify-content-between mb-4">
                <Col xxl={3} lg={6}>
                  <div className="search-box mb-3 mb-lg-0">
                    <Form.Control
                      type="text"
                      placeholder="Search Category..."
                      onChange={searchTeamMember}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </Col>
              </Row>

              <Row id="categories-list">
                {currentpages.map((item, key) => (
                  <Col xxl={4} md={6} key={key}>
                    <Card className="categrory-widgets overflow-hidden">
                      <Card.Body className="p-4">
                        {item.imageUrl && (
                          <div className="text-center p-2">
                            <img
                              src={item.imageUrl}
                              alt={item.name}
                              className="img-fluid rounded"
                              style={{ maxHeight: "150px", width: "auto" }}
                            />
                          </div>
                        )}
                        <div className="d-flex align-items-center mb-3">
                          <h5 className="flex-grow-1 mb-0">{item.name}</h5>
                          <ul className="flex-shrink-0 list-unstyled hstack gap-1 mb-0">
                            <li>
                              <Link
                                to="#"
                                className="badge bg-info-subtle text-info"
                                onClick={() => handleEditClick(item)}
                              >
                                Edit
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#"
                                className="badge bg-danger-subtle text-danger"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleDeleteClick(item._id);
                                }}
                              >
                                Delete
                              </Link>
                            </li>
                          </ul>
                        </div>
                        <p className="text-muted">{item.description}</p>
                        <div className="mt-3">
                          <Link
                            to="#"
                            className="fw-medium link-effect"
                            onClick={() => {
                              setShow(true);
                              setInfo(item);
                            }}
                          >
                            Read More{" "}
                            <i className="ri-arrow-right-line align-bottom ms-1"></i>
                          </Link>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* {Update Category} */}

              <Offcanvas
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                placement="end"
              >
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title>
                    Edit Category: {editingCategory?.name}
                  </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <form onSubmit={handleUpdateSubmit}>
                    <Row>
                      <Col xxl={12}>
                        <div className="mb-3">
                          <label htmlFor="name" className="form-label">
                            Category Name<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="name"
                            placeholder="Enter name"
                            required
                            value={editFormData.name}
                            onChange={handleEditInputChange}
                          />
                          <div className="invalid-feedback">
                            Please enter a category name.
                          </div>
                        </div>
                      </Col>

                      <Col xxl={12}>
                        <div className="mb-3">
                          <label htmlFor="slug" className="form-label">
                            Slug
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="slug"
                            placeholder="Enter slug"
                            value={editFormData.slug}
                            onChange={handleEditInputChange}
                          />
                        </div>
                      </Col>

                      <Col xxl={12}>
                        <div className="mb-3">
                          <label htmlFor="description" className="form-label">
                            Description<span className="text-danger">*</span>
                          </label>
                          <textarea
                            className="form-control"
                            id="description"
                            rows={3}
                            placeholder="Description"
                            required
                            value={editFormData.description}
                            onChange={handleEditInputChange}
                          ></textarea>
                          <div className="invalid-feedback">
                            Please enter a description.
                          </div>
                        </div>
                      </Col>

                      <Col xxl={12}>
                        <div className="mb-3">
                          <label className="form-label">Current Products</label>
                          {editingCategory?.products?.length > 0 ? (
                            <div className="border p-2 rounded">
                              {getProductNamesByIds(
                                editingCategory.products
                              ).map((name, index) => (
                                <div
                                  key={index}
                                  className="badge bg-success me-1 mb-1"
                                >
                                  {name}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-muted">
                              No products in this category
                            </div>
                          )}
                        </div>
                      </Col>

                      <Col xxl={12}>
                        <div className="mb-3">
                          <label htmlFor="productSelect" className="form-label">
                            Update Products
                          </label>
                          <Select
                            isMulti
                            options={productOptions}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            onChange={(selectedOptions) => {
                              const selectedIds = selectedOptions.map(
                                (opt) => opt.value
                              );
                              setEditSelectedProductIds(selectedIds);
                            }}
                            value={productOptions.filter((option) =>
                              editSelectedProductIds.includes(option.value)
                            )}
                          />
                        </div>
                      </Col>

                      <Col xxl={12}>
                        <div className="mb-3">
                          <label
                            htmlFor="editCategoryImage"
                            className="form-label"
                          >
                            Update Category Image
                          </label>
                          <input
                            type="file"
                            className="form-control"
                            id="editCategoryImage"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                setEditSelectedImage(e.target.files[0]);
                              }
                            }}
                          />
                          {editSelectedImage ? (
                            <div className="mt-2">
                              <img
                                src={URL.createObjectURL(editSelectedImage)}
                                alt="New Preview"
                                className="img-thumbnail"
                                style={{ maxHeight: "150px" }}
                              />
                            </div>
                          ) : (
                            editingCategory?.imageUrl && (
                              <div className="mt-2">
                                <p>Current Image:</p>
                                <img
                                  src={editingCategory.imageUrl}
                                  alt="Current Preview"
                                  className="img-thumbnail"
                                  style={{ maxHeight: "150px" }}
                                />
                              </div>
                            )
                          )}
                        </div>
                      </Col>

                      <Col xxl={12}>
                        <div className="form-check mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isActive"
                            checked={editFormData.isActive}
                            onChange={handleEditCheckboxChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="isActive"
                          >
                            Is Active
                          </label>
                        </div>
                      </Col>

                      <Col xxl={12}>
                        <div className="text-end">
                          <Button
                            variant="primary"
                            type="submit"
                            className="me-2"
                            disabled={isUpdating}
                          >
                            {isUpdating ? "Updating..." : "Update Category"}
                          </Button>
                          <Button
                            variant="light"
                            onClick={() => setShowEditModal(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </form>
                </Offcanvas.Body>
              </Offcanvas>

              {/* {Delete Canvas} */}
              {/* Delete Confirmation Modal */}
              <Offcanvas
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                placement="end"
              >
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title>Confirm Delete</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <p>
                    Are you sure you want to delete this category? This action
                    cannot be undone.
                  </p>
                  <div className="text-end">
                    <Button
                      variant="danger"
                      onClick={confirmDelete}
                      className="me-2"
                    >
                      Yes, Delete
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowDeleteModal(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </Offcanvas.Body>
              </Offcanvas>

              {/* Pagination */}
              {pagination && (
                <Row className="mb-4">
                  <Col lg={12}>
                    <div className="pagination-block pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                      <div
                        className={
                          currentPage <= 1 ? "page-item disabled" : "page-item"
                        }
                      >
                        <Button
                          variant="link"
                          className="page-link"
                          onClick={handleprevPage}
                        >
                          ←
                        </Button>
                      </div>
                      {pageNumbers.map((item, key) => (
                        <div
                          key={key}
                          className={
                            currentPage === item
                              ? "page-item active"
                              : "page-item"
                          }
                        >
                          <Link
                            className="page-link"
                            to="#"
                            id={item.toString()}
                            onClick={handleClick}
                          >
                            {item}
                          </Link>
                        </div>
                      ))}
                      <div
                        className={
                          currentPage >= pageNumbers.length
                            ? "page-item disabled"
                            : "page-item"
                        }
                      >
                        <Button
                          variant="link"
                          className="page-link"
                          onClick={handlenextPage}
                        >
                          →
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas show={show} onHide={() => setShow(false)} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{info.name}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {info.imageUrl && (
            <div className="text-center mb-3">
              <img
                src={info.imageUrl}
                alt={info.name}
                className="img-fluid rounded"
                style={{ maxHeight: "200px" }}
              />
            </div>
          )}
          <p className="text-muted">{info.description}</p>
          <p className="text-muted">Slug: {info.slug}</p>
          <p className="text-muted">
            Status: {info.isActive ? "Active" : "Inactive"}
          </p>
        </Offcanvas.Body>
      </Offcanvas>
    </React.Fragment>
  );
};

export default Categories;
